import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SavedLocationsModal from "../../components/modals/SavedLocationsModal";
import YuJaButton from "../../components/standardization/YuJaButton";
import { YuJaDropdown } from "../../components/standardization/YuJaDropdown";
import { YuJaRadioBox } from "../../components/standardization/YuJaRadiobox";
import { useLocalStorageNew } from "../../hooks/useLocalStorageNew";
import {ReactComponent as PollDetailsIcon} from "../../images/poll_details.svg";
import {ReactComponent as QuestionDetailsIcon} from "../../images/question_details.svg";
import {ReactComponent as ToastIcon} from "../../images/toast_icon.svg";
import {ReactComponent as UserSettingsIcon} from "../../images/user_settings_icon.svg";

import {
    DEFAULT_LANGUAGE,
    DEFAULT_ATTENDANCE_SAVED_LOCATIONS,
    DEFAULT_CLASS_RESULT_TIME_LIMIT,
    DEFAULT_ENTRIES_PER_PARTICIPANT,
    DEFAULT_GET_READY_ALL_TIME_LIMIT,
    DEFAULT_GET_READY_TIME_LIMIT,
    DEFAULT_POINTS,
    DEFAULT_POLL_BREAK_TIME_LIMIT,
    DEFAULT_TIME_LIMIT,
    LMS_GRADEBOOK_SYNC,
    RECORD_ATTEMPT,
    SYNC_ATTEMPT,
    TEXTBOX,
    USER_SETTINGS,
    WC_MAXIMUM_ENTRIES,
    YUJA_DROPDOWN_SIZE,
    DEFAULT_POLL_TIME_LIMIT,
    DEFAULT_ATTENDANCE_TIME_LIMIT,
    DEFAULT_COUNT_DOWN_TIME_LIMIT,
} from "../../utils/constants";
import {deCapitalizeWord, isParticipationPointEnabled, notifyError, notifySuccess} from "../../utils/helpers";

import classes from "./Settings.module.css";
import {UserApiCalls} from "../../apis/UserApiCalls";
import DropdownTimeLimit from "../../components/DropdownTimeLimit";
import DropdownWeightage from "../../components/DropdownWeightage";
import {YuJaTextBox} from "../../components/standardization/YuJaTextBox";
import TimeLimitInput from "../../components/TimeLimitInput";
import YuJaSwitch from "../../components/standardization/YuJaSwitch";

export default function UserSettingTab({userSettings, setUserSettings, isViewer=false}) {
    const { t } = useTranslation();
    const [, getSession] = useLocalStorageNew("session", {});
    const session = getSession();
    const [showSavedLocationModal, setShowSavedLocationModal] = useState(false);
    // savedLocations is an array of {name: string, lat: number, lng: number, rad: number}
    const [savedLocations, setSavedLocations] = useState(JSON.parse(userSettings && userSettings.hasOwnProperty(USER_SETTINGS.ATTENDANCE_SAVED_LOCATIONS) ? userSettings[USER_SETTINGS.ATTENDANCE_SAVED_LOCATIONS] : DEFAULT_ATTENDANCE_SAVED_LOCATIONS));
    const optionArr = [
        {value: "5", label: t("settings.toastDuration5s")},
        {value: "10", label: t("settings.toastDuration10s")},
        {value: "-1", label: t("settings.toastDismissManually")},
    ];

    const languageOptions = [
        {value: "en", label: t("settings.language.options.en")},
        {value: "fr", label: t("settings.language.options.fr")},
    ];

    const onSelectLanguage = (item) => {
        const copy = JSON.parse(JSON.stringify(userSettings));
        copy[USER_SETTINGS.LANGUAGE] = item.value;
        setUserSettings(copy);
    };

    const attemptSettingsArr = [
        LMS_GRADEBOOK_SYNC.FIRST,
        LMS_GRADEBOOK_SYNC.RECENT,
        LMS_GRADEBOOK_SYNC.HIGHEST
    ].map(option => ({...option, label: t(option.labelKey)}));
    const recordAttemptOptions = useRef([ RECORD_ATTEMPT.ALL, RECORD_ATTEMPT.RECENT ]);

    const [maxAttempts, setMaxAttempts] = useState(!!userSettings[USER_SETTINGS.POLL_MAX_ATTEMPTS]
        ? `${userSettings[USER_SETTINGS.POLL_MAX_ATTEMPTS]}`
        : "-1"
    );

    const {saveSettings} = UserApiCalls();

    const onSelect = (item, setting) => {
        const copy = JSON.parse(JSON.stringify(userSettings));
        copy[USER_SETTINGS.TOAST_MESSAGE_DURATION] = item.value;
        setUserSettings(copy);
    };

    const handleLMSAttemptSettingChange = (item) => {
        const copy = JSON.parse(JSON.stringify(userSettings));
        copy[USER_SETTINGS.POLL_LMS_SYNC] = item.value;
        setUserSettings(copy);
    };

    const onChangeCustomEntries = (e) => {
        const copy = JSON.parse(JSON.stringify(userSettings));
        copy[USER_SETTINGS.QUESTION_ENTRIES] = e.target.value;

        setUserSettings(copy);
    };

    const onChangeCustomGetReadyTime = (event) => {
        let value = event.target.value.replace(/[^0-9]/g, '');
        value = parseInt(value, 10);
        const copy = JSON.parse(JSON.stringify(userSettings));
        if(!isNaN(value) && value < 100 && value > 0) {
            copy[USER_SETTINGS.QUESTION_COUNTDOWN] = value;
        }
        else {
            copy[USER_SETTINGS.QUESTION_COUNTDOWN] = "";
        }
        setUserSettings(copy);
    };


    const handleRecordAttemptOptionClick = (value) => {
        const copy = JSON.parse(JSON.stringify(userSettings));
        if (value === RECORD_ATTEMPT.RECENT.value) {
            copy[USER_SETTINGS.POLL_RECORD_ATTEMPTS] = SYNC_ATTEMPT.RECENT.value;
            copy[USER_SETTINGS.POLL_LMS_SYNC] = LMS_GRADEBOOK_SYNC.RECENT.value; 
        } else if (value === RECORD_ATTEMPT.ALL.value) {
            copy[USER_SETTINGS.POLL_RECORD_ATTEMPTS] = RECORD_ATTEMPT.ALL.value;
            const {settings={}} = session;
            if (settings.hasOwnProperty(USER_SETTINGS.POLL_LMS_SYNC)) {
                copy[USER_SETTINGS.POLL_LMS_SYNC] =settings[USER_SETTINGS.POLL_LMS_SYNC]; 
            }
        }
        setUserSettings(copy);
    };


    const saveUserSettings = (settings) => {
        saveSettings(settings).then(() => {
            notifySuccess(t("settings.saveSuccess"));
        });
    };

    return (
        <>
            <SavedLocationsModal
                show={showSavedLocationModal}
                setShow={setShowSavedLocationModal}
                savedLocations={savedLocations}
                setSavedLocations={setSavedLocations}
                userSettings={userSettings}
                setUserSettings={setUserSettings}
                saveSettingsCallback={saveUserSettings}
            />

            <div className={isViewer ? classes.viewerSettingCards : classes.userSettingColumn}>
                <div className={classes.cardBox} style={{width: "100%"}}>
                    <div className={classes.cardBoxHeader}>
                        <UserSettingsIcon/>
                        <span>{t("settings.title")}</span>
                    </div>
                    <div className={classes.cardBoxBody}>
                        <div className={classes.settingRow}>
                            <div>
                                <div className={classes.settingLabel}>{t("settings.language.title")}</div>
                                <div className={classes.settingDescription}>{t("settings.language.description")}</div>
                            </div>
                            <div style={{flex: 1}}/>
                            <YuJaDropdown
                                ariaLabel={t("settings.language.title")}
                                containerStyle={{maxWidth: 250}}
                                data={languageOptions}
                                value={userSettings[USER_SETTINGS.LANGUAGE] || DEFAULT_LANGUAGE}
                                onChange={onSelectLanguage}
                            />
                        </div>
                    </div>
                </div>

                <div className={classes.cardBox} style={{width: "100%"}}>
                    <div className={classes.cardBoxHeader}>
                        <ToastIcon/>
                        <span>{t("institution.toastMessageSettings")}</span>
                    </div>
                    <div className={classes.cardBoxBody}>
                        <div className={classes.settingRow}>
                            <span className={classes.cardDescription}>{t("institution.userToastMessageLabel")}</span>
                        </div>
                        <YuJaDropdown
                            ariaLabel={t("accessibility.toastDropdown") + userSettings[USER_SETTINGS.TOAST_MESSAGE_DURATION] + t("questionBuilder.secondsLabel")}
                            containerStyle={{maxWidth: 250}}
                            data={optionArr}
                            value={userSettings[USER_SETTINGS.TOAST_MESSAGE_DURATION]}
                            onChange={item => onSelect(item, USER_SETTINGS.TOAST_MESSAGE_DURATION)}
                        />
                    </div>
                </div>

                {!isViewer && <>
                    <div className={classes.cardBox} style={{flex: 1}}>
                        <div className={classes.cardBoxHeader}>
                            <QuestionDetailsIcon/>
                            <span role={TEXTBOX}>{t("institution.questionDetailsSettings")}</span>
                        </div>
                        <div className={classes.cardBoxBody}>
                            <span className={classes.cardDescription}>{t("institution.userQuestionMessageLabel")}</span>

                            <div className={classes.settingRow}>
                                <div className={classes.settingRow}>
                                    <div>
                                        <div className={classes.settingLabel}> {t("institution.questionDurationLabel")}</div>
                                        <div
                                            className={classes.settingDescription}>{t("institution.questionDurationDescription")}</div>
                                    </div>

                                    <div style={{flex: 1}}/>

                                    <DropdownTimeLimit
                                        defaultWidth={170}
                                        timeLimit={userSettings.hasOwnProperty(USER_SETTINGS.QUESTION_DURATION)
                                            ? parseInt(userSettings[USER_SETTINGS.QUESTION_DURATION])
                                            : DEFAULT_TIME_LIMIT
                                        }
                                        setTimeLimit={(timeLimit) => {
                                            const copy = JSON.parse(JSON.stringify(userSettings));
                                            copy[USER_SETTINGS.QUESTION_DURATION] = timeLimit;
                                            setUserSettings(copy);
                                        }}
                                    />
                                </div>


                                {/*{!!userSettings[USER_SETTINGS.QUESTION_DURATION] && !!parseInt(userSettings[USER_SETTINGS.QUESTION_DURATION]) &&*/}
                                {/*    <DropdownTimeLimit*/}
                                {/*        timeLimit={userSettings.hasOwnProperty(USER_SETTINGS.QUESTION_DURATION)*/}
                                {/*            ? parseInt(userSettings[USER_SETTINGS.QUESTION_DURATION])*/}
                                {/*            : DEFAULT_TIME_LIMIT*/}
                                {/*        }*/}
                                {/*        setTimeLimit={(timeLimit) => {*/}
                                {/*            const copy = JSON.parse(JSON.stringify(userSettings));*/}
                                {/*            copy[USER_SETTINGS.QUESTION_DURATION] = timeLimit;*/}
                                {/*            setUserSettings(copy);*/}
                                {/*        }}*/}
                                {/*    />*/}
                                {/*}*/}
                            </div>


                            <div className={classes.settingRow}>
                                <div className={classes.settingRow}>
                                    <div>
                                        <div className={classes.settingLabel}>{t("questionBuilder.correctnessPointsLabel")}</div>
                                        <div className={classes.settingDescription}>{t("questionBuilder.correctnessPointsDescription")}</div>
                                    </div>
                                    <div style={{flex: 1}}/>
                                    <DropdownWeightage
                                        defaultWidth={170}
                                        weightage={userSettings.hasOwnProperty(USER_SETTINGS.QUESTION_POINTS)
                                            ? parseInt(userSettings[USER_SETTINGS.QUESTION_POINTS])
                                            : DEFAULT_POINTS}
                                        setWeightage={(weightage) => {
                                            const copy = JSON.parse(JSON.stringify(userSettings));
                                            copy[USER_SETTINGS.QUESTION_POINTS] = weightage;
                                            setUserSettings(copy);
                                        }}
                                        isFromSettingsPage={true}
                                    />
                                </div>


                                {/*{!!userSettings[USER_SETTINGS.QUESTION_POINTS] && !!parseInt(userSettings[USER_SETTINGS.QUESTION_POINTS]) &&*/}
                                {/*    <DropdownWeightage*/}
                                {/*        weightage={userSettings.hasOwnProperty(USER_SETTINGS.QUESTION_POINTS)*/}
                                {/*            ? parseInt(userSettings[USER_SETTINGS.QUESTION_POINTS])*/}
                                {/*            : DEFAULT_POINTS}*/}
                                {/*        setWeightage={(weightage) => {*/}
                                {/*            const copy = JSON.parse(JSON.stringify(userSettings));*/}
                                {/*            copy[USER_SETTINGS.QUESTION_POINTS] = weightage;*/}
                                {/*            setUserSettings(copy);*/}
                                {/*        }}*/}
                                {/*        isFromSettingsPage={true}*/}
                                {/*    />*/}

                                {/*}*/}
                            </div>


                            <div className={classes.settingRow} style={{flexDirection: "column", alignItems: "unset", gap: 10}}>
                                <div className={classes.settingRow}>
                                    <div>
                                        <div className={classes.settingLabel}>{t("questionBuilder.participationPointsLabel")}</div>
                                        <div className={classes.settingDescription}>{t("questionBuilder.participationPointsDescription")}</div>
                                    </div>
                                    <div style={{flex: 1}}/>

                                    <YuJaSwitch
                                        aria-label={t("questionBuilder.participationPointsLabel")}
                                        checked={isParticipationPointEnabled(userSettings)}
                                        onChange={checked => {
                                            const copy = JSON.parse(JSON.stringify(userSettings));
                                            copy[USER_SETTINGS.QUESTION_PARTICIPATION_POINTS] = checked ? DEFAULT_POINTS : -1;
                                            setUserSettings(copy);
                                        }}
                                    />
                                </div>


                                {isParticipationPointEnabled(userSettings) &&
                                    <DropdownWeightage
                                        weightage={userSettings.hasOwnProperty(USER_SETTINGS.QUESTION_PARTICIPATION_POINTS)
                                            ? parseInt(userSettings[USER_SETTINGS.QUESTION_PARTICIPATION_POINTS])
                                            : DEFAULT_POINTS}
                                        setWeightage={(weightage) => {
                                            const copy = JSON.parse(JSON.stringify(userSettings));
                                            copy[USER_SETTINGS.QUESTION_PARTICIPATION_POINTS] = weightage;
                                            setUserSettings(copy);
                                        }}
                                        isFromSettingsPage={true}
                                    />

                                }
                            </div>

                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>{t("questionBuilder.countdownTimerLabel")}</div>
                                    <div className={classes.settingDescription}>{t("questionBuilder.questionCountdownTimerDescription")}</div>
                                </div>
                                <div style={{flex: 1}}/>

                                <TimeLimitInput
                                    timeLimit={userSettings[USER_SETTINGS.QUESTION_COUNTDOWN]}
                                    setTimeLimit={(timeLimit) => {
                                        const copy = JSON.parse(JSON.stringify(userSettings));
                                        copy[USER_SETTINGS.QUESTION_COUNTDOWN] = timeLimit;
                                        setUserSettings(copy);
                                    }}
                                    unit={"MM:SS"}
                                    minVal={DEFAULT_COUNT_DOWN_TIME_LIMIT}
                                    reachMinValErrorText={t("settings.minimumCountdownError")}
                                />
                            </div>

                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>{t("questionBuilder.maximumEntriesLabel")}</div>
                                    <div className={classes.settingDescription}>{t("questionBuilder.maximumEntriesDescription")}</div>
                                </div>
                                <div style={{flex: 1}}/>

                                <YuJaTextBox
                                    containerStyle={{width: 50, height: 35}}
                                    inputAlign={"center"}
                                    min="1"
                                    max="10"
                                    value={`${userSettings[USER_SETTINGS.QUESTION_ENTRIES]}`}
                                    onChange={onChangeCustomEntries}
                                    onBlur={() => {
                                        const copy = JSON.parse(JSON.stringify(userSettings));
                                        if (userSettings[USER_SETTINGS.QUESTION_ENTRIES] === "") {
                                            copy[USER_SETTINGS.QUESTION_ENTRIES] = DEFAULT_ENTRIES_PER_PARTICIPANT;
                                            notifyError(t("settings.entriesMinError"));
                                        } else if (userSettings[USER_SETTINGS.QUESTION_ENTRIES] > WC_MAXIMUM_ENTRIES) {
                                            parseInt(userSettings[USER_SETTINGS.QUESTION_ENTRIES])
                                            copy[USER_SETTINGS.QUESTION_ENTRIES] = WC_MAXIMUM_ENTRIES;
                                            notifyError(t("settings.entriesMaxError"));
                                        } else if (userSettings[USER_SETTINGS.QUESTION_ENTRIES] <= 0) {
                                            copy[USER_SETTINGS.QUESTION_ENTRIES] = DEFAULT_ENTRIES_PER_PARTICIPANT;
                                            notifyError(t("settings.entriesMinError"));
                                        } else {
                                            copy[USER_SETTINGS.QUESTION_ENTRIES] = parseInt(userSettings[USER_SETTINGS.QUESTION_ENTRIES]);
                                        }
                                        setUserSettings(copy);

                                    }}
                                    placeholder={"1"}
                                    type={"number"}
                                />
                            </div>


                            {/*class result duration*/}
                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>
                                        {t("questionBuilder.classResultDurationLabel")}
                                    </div>
                                    <div className={classes.settingDescription}>
                                        {t("poll.participantsViewClassResultDescription")}
                                    </div>
                                </div>
                                <div style={{flex: 1}}/>
                                <TimeLimitInput
                                    timeLimit={userSettings[USER_SETTINGS.QUESTION_CLASS_RESULT_DURATION]}
                                    setTimeLimit={(timeLimit) => {
                                        const copy = JSON.parse(JSON.stringify(userSettings));
                                        copy[USER_SETTINGS.QUESTION_CLASS_RESULT_DURATION] = timeLimit;
                                        setUserSettings(copy);
                                    }}
                                    unit={"MM:SS"}
                                    minVal={DEFAULT_CLASS_RESULT_TIME_LIMIT}
                                    reachMinValErrorText={t("settings.minimumClassResultDurationError")}
                                />
                            </div>
                        </div>
                    </div>
                </>
                }

            </div>
            {!isViewer &&
                <div className={classes.userSettingColumn}>
                    <div className={classes.cardBox} style={{width: "100%"}}>
                        <div className={classes.cardBoxHeader}>
                            <PollDetailsIcon/>
                            <span>{t("institution.activityDetailsSettings")}</span>
                        </div>
                        <div className={classes.cardBoxBody}>
                            <span className={classes.cardDescription}>{t("institution.activityDetailsSettingsDescription")}</span>

                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>{t("questionBuilder.countdownTimerLabel")}</div>
                                    <div className={classes.settingDescription}>{t("questionBuilder.pollCountdownTimerDescription")}</div>
                                </div>
                                <div style={{flex: 1}}/>

                                <TimeLimitInput
                                    timeLimit={userSettings[USER_SETTINGS.POLL_COUNTDOWN]}
                                    setTimeLimit={(timeLimit) => {
                                        const copy = JSON.parse(JSON.stringify(userSettings));
                                        copy[USER_SETTINGS.POLL_COUNTDOWN] = timeLimit;
                                        setUserSettings(copy);
                                    }}
                                    unit={"MM:SS"}
                                    minVal={DEFAULT_COUNT_DOWN_TIME_LIMIT}
                                    reachMinValErrorText={t("settings.minimumCountdownError")}
                                />
                            </div>
                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>{t("poll.requireParticipantsName")}</div>
                                    <div
                                        className={classes.settingDescription}>{t("poll.requireParticipantsNameDescription")}</div>
                                </div>
                                <div style={{flex: 1}}/>

                                <YuJaSwitch
                                    aria-label={t("poll.requireParticipantsName")}
                                    checked={userSettings[USER_SETTINGS.POLL_REQUIRED_NAME] === "true" || userSettings[USER_SETTINGS.POLL_REQUIRED_NAME] === true}
                                    onChange={checked => {
                                        const copy = JSON.parse(JSON.stringify(userSettings));
                                        copy[USER_SETTINGS.POLL_REQUIRED_NAME] = checked;
                                        setUserSettings(copy);
                                    }}
                                />
                            </div>

                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>{t("questionBuilder.showCorrectAnswerTitle")}</div>
                                    <div className={classes.settingDescription}>{t("questionBuilder.showCorrectAnswerDescription")}</div>
                                </div>
                                <div style={{flex: 1}}/>
                                <YuJaSwitch
                                    aria-label={t("questionBuilder.showCorrectAnswerTitle")}
                                    checked={userSettings[USER_SETTINGS.POLL_SHOW_CORRECT_ANS] === "true" || userSettings[USER_SETTINGS.POLL_SHOW_CORRECT_ANS] === true}
                                    onChange={checked => {
                                        const copy = JSON.parse(JSON.stringify(userSettings));
                                        copy[USER_SETTINGS.POLL_SHOW_CORRECT_ANS] = checked;
                                        setUserSettings(copy);
                                    }}
                                />
                            </div>

                            <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                                <span className={classes.settingLabel}> {t("gradebook.recordAttemptTitle")} </span>
                                <div style={{display: "flex", flexDirection: "column", gap: 10}}>
                                    {Object.entries(recordAttemptOptions.current).map((optionData, index) => {
                                        const {value, labelKey} = optionData[1];
                                        const label = t(labelKey);
                                        return (
                                            <YuJaRadioBox
                                                key={index}
                                                aria-label={label + t("accessibility.radialButtonSuffix") + (value === userSettings[USER_SETTINGS.POLL_RECORD_ATTEMPTS] ? "selected" : "unselected")}
                                                label={label}
                                                style={{width: "fit-content"}}
                                                checked={value === userSettings[USER_SETTINGS.POLL_RECORD_ATTEMPTS]}
                                                onClick={() => handleRecordAttemptOptionClick(value)}
                                            />
                                        );
                                    })}
                                </div>
                            </div>

                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>{t("poll.gradeBookLabel")}</div>
                                    <div
                                        className={classes.settingDescription}>{t("gradebook.attemptSyncToGradeBookDescription")}</div>
                                </div>

                                <div style={{flex: 1}}/>

                                <YuJaDropdown
                                    ariaLabel={t("accessibility.attemptDropdown") + userSettings[USER_SETTINGS.POLL_LMS_SYNC]}
                                    containerStyle={{width: 250, flexShrink: 0}}
                                    data={attemptSettingsArr}
                                    value={userSettings[USER_SETTINGS.POLL_LMS_SYNC]}
                                    onChange={handleLMSAttemptSettingChange}
                                    size={YUJA_DROPDOWN_SIZE.SMALL}
                                    disabled={userSettings[USER_SETTINGS.POLL_RECORD_ATTEMPTS] === SYNC_ATTEMPT.RECENT.value}
                                />
                            </div>

                            {/*Poll max Attempts*/}
                            <div className={classes.settingRow} style={{flexDirection: "column", alignItems: "unset"}}>
                                <div className={classes.settingRow}>
                                    <div>
                                        <div className={classes.settingLabel}>{t("gradebook.pollAttemptsLabel")}</div>
                                        <div
                                            className={classes.settingDescription}>{t("gradebook.unlimitedMaxAttemptsSettingText")}</div>
                                    </div>

                                    <div style={{flex: 1}}/>
                                    <YuJaSwitch
                                        aria-label={t("gradebook.pollAttemptsLabel")}
                                        checked={!userSettings[USER_SETTINGS.POLL_MAX_ATTEMPTS] || parseInt(userSettings[USER_SETTINGS.POLL_MAX_ATTEMPTS]) < 0}
                                        onChange={checked => {
                                            const copy = JSON.parse(JSON.stringify(userSettings));
                                            if (checked) {
                                                copy[USER_SETTINGS.POLL_MAX_ATTEMPTS] = -1;
                                                setMaxAttempts(-1);
                                            }
                                            else {
                                                copy[USER_SETTINGS.POLL_MAX_ATTEMPTS] = 1;
                                                setMaxAttempts(1);
                                            }
                                            setUserSettings(copy);
                                        }}
                                    />
                                </div>

                                {!!userSettings[USER_SETTINGS.POLL_MAX_ATTEMPTS] && parseInt(userSettings[USER_SETTINGS.POLL_MAX_ATTEMPTS]) > 0 &&
                                    <div style={{display: "flex", alignItems: "center", gap: 15}}>
                                        <span
                                            className={classes.settingDescription}>{t("questionBuilder.setMaxAttempts")}</span>
                                        <div style={{flex: 1}}/>
                                        <YuJaTextBox
                                            containerStyle={{width: 50, height: 35}}
                                            inputAlign={"center"}
                                            value={maxAttempts}
                                            onChange={(e) => setMaxAttempts(e.target.value)}
                                            placeholder={"1"}
                                            min="1"
                                            type={"number"}
                                            onBlur={() => {
                                                const copy = JSON.parse(JSON.stringify(userSettings));
                                                if (!maxAttempts) {
                                                    copy[USER_SETTINGS.POLL_MAX_ATTEMPTS] = -1;
                                                } else if (parseInt(maxAttempts) > 0) {
                                                    copy[USER_SETTINGS.POLL_MAX_ATTEMPTS] = parseInt(maxAttempts);
                                                    setMaxAttempts(`${parseInt(maxAttempts)}`);
                                                } else {
                                                    copy[USER_SETTINGS.POLL_MAX_ATTEMPTS] = -1;
                                                }
                                                setUserSettings(copy);

                                            }}
                                        />
                                    </div>
                                }
                            </div>


                            {/*Activity time limit*/}
                            <div className={classes.settingRow} style={{flexDirection: "column"}}>
                                <div className={classes.settingRow}>
                                    <div>
                                        <div className={classes.settingLabel}>{t("gradebook.activityLengthSettingText")}</div>
                                        <div className={classes.settingDescription}>{t("gradebook.activityLengthSettingDesc")}</div>
                                    </div>

                                    <div style={{flex: 1}}/>
                                    <YuJaSwitch
                                        aria-label={t("gradebook.activityLengthSettingText")}
                                        checked={userSettings[USER_SETTINGS.ATTENDANCE_MANUAL_END] === "true" || userSettings[USER_SETTINGS.ATTENDANCE_MANUAL_END] === true}
                                        onChange={checked => {
                                            const copy = JSON.parse(JSON.stringify(userSettings));
                                            copy[USER_SETTINGS.ATTENDANCE_MANUAL_END] = checked;
                                            if (!copy[USER_SETTINGS.POLL_DURATION]) {
                                                copy[USER_SETTINGS.POLL_DURATION] = DEFAULT_POLL_TIME_LIMIT;
                                            }

                                            if (!copy[USER_SETTINGS.ATTENDANCE_DURATION]) {
                                                copy[USER_SETTINGS.ATTENDANCE_DURATION] = DEFAULT_ATTENDANCE_TIME_LIMIT;
                                            }
                                            setUserSettings(copy);
                                        }}
                                    />

                                </div>

                                {(userSettings[USER_SETTINGS.ATTENDANCE_MANUAL_END] === "false" || userSettings[USER_SETTINGS.ATTENDANCE_MANUAL_END] === false) &&
                                    <div className={classes.settingRow}>
                                        <div>
                                            <div className={classes.settingLabel}>
                                                {t("questionBuilder.activityTimeLimitText")}
                                            </div>
                                            <div className={classes.settingDescription}>
                                                {t("poll.participantTimeIntro", {activityType: deCapitalizeWord(t("common.activityWord"))})}
                                            </div>
                                        </div>
                                        <div style={{flex: 1}}/>
                                        <TimeLimitInput
                                            timeLimit={userSettings[USER_SETTINGS.POLL_DURATION]}
                                            setTimeLimit={(timeLimit) => {
                                                const copy = JSON.parse(JSON.stringify(userSettings));
                                                copy[USER_SETTINGS.POLL_DURATION] = timeLimit;
                                                if (!timeLimit) {
                                                    copy[USER_SETTINGS.ATTENDANCE_MANUAL_END] = true;
                                                }
                                                setUserSettings(copy);
                                            }}
                                            unit={"HH:MM"}
                                        />
                                    </div>

                                }
                            </div>


                            {/*poll break duration*/}
                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>
                                        {t("questionBuilder.pollBreakDurationLabel")}
                                    </div>
                                    <div className={classes.settingDescription}>
                                        {t("poll.pollEndFlexibleTimeDescription", {activityType: deCapitalizeWord(t("poll.mergedModeText"))})}
                                    </div>
                                </div>

                                <div style={{flex: 1}}/>
                                <TimeLimitInput
                                    timeLimit={userSettings[USER_SETTINGS.POLL_BREAK_DURATION]}
                                    setTimeLimit={(timeLimit) => {
                                        const copy = JSON.parse(JSON.stringify(userSettings));
                                        copy[USER_SETTINGS.POLL_BREAK_DURATION] = timeLimit;
                                        setUserSettings(copy);
                                    }}
                                    unit={"MM:SS"}
                                    minVal={DEFAULT_POLL_BREAK_TIME_LIMIT}
                                    reachMinValErrorText={t("settings.minimumBreakDurationError")}
                                />

                            </div>


                            <div className={classes.settingRow}>
                                <div>
                                    <div className={classes.settingLabel}>
                                        {t("gradebook.geofencedLocationsTitle")}
                                    </div>
                                    <div className={classes.settingDescription}>
                                        {t("institution.attendanceSettingsGeoLocationSubTitle")}
                                    </div>
                                </div>
                                <div style={{flex: 1}}/>
                                <YuJaButton
                                    style={{whiteSpace: "nowrap"}}
                                    onClick={() => setShowSavedLocationModal(true)}
                                >
                                    {t("geofence.configureLocationsText")}
                                </YuJaButton>
                            </div>
                        </div>
                    </div>
                </div>
            }
        </>
    );
}